import * as React from 'react';
import axios from 'axios';
import { buildGoogleMapsAddress } from 'utils';
import { ListingPhotoFile, JsonObject } from '@bb/domain/dist/types';
import type { CurrentUser } from '@/app-package';
import { ListingDetailsComponents } from 'components';
import { GetListingFilesResponse } from 'api/FileController/list';
import { User } from '@bb/users';
import { ListingDoc, OrgDoc } from '@bb/domain/dist/schemas';
import {
  FinancialDetails,
  IncludedAndExcludedList,
  ListingDetails,
  ListingTitle,
  MoreInfoList,
  PropertyDetails,
  PropertyInfo,
  RequiredList,
  RoomDetails,
} from '../../DashboardComponents/ListingViewComponents';
import ListingViewCarousel from '../../DashboardComponents/ListingViewCarousel';
import {
  CarouselAsset,
  CarouselImageAsset,
} from '../../DashboardComponents/ListingViewCarousel/types';
import { Loading } from '../../react-common';

type ListingViewContainerProps = {
  listing: JsonObject<
    Omit<ListingDoc, 'reps' | 'org'> & { reps: User[]; org: OrgDoc }
  >;
  auth?: CurrentUser;
};

type ListingViewContainerState = {
  assets: CarouselAsset[];
  attachments: ListingDetailsComponents.PublicAttachmentType[];
  isLoading: boolean;
  enableOpenHouses: boolean;
};

class ListingViewContainer extends React.Component<
  ListingViewContainerProps,
  ListingViewContainerState
> {
  constructor(props: ListingViewContainerProps) {
    super(props);

    this.state = {
      assets: [],
      attachments: [],
      isLoading: true,
      enableOpenHouses: false,
    };
  }

  async componentDidMount() {
    const { listing } = this.props;
    const [
      { data: files },
      {
        data: { enableOpenHouses },
      },
    ] = await Promise.all([
      axios.get<GetListingFilesResponse>(`/listing/${listing.id}/file`),
      axios.get<{ enableOpenHouses?: boolean }>('/org/settings'),
    ]);

    const images = files.filter(file => file.isPhoto) as ListingPhotoFile[];
    const attachments = files
      .filter(file => file.isAttachment)
      .map(file => ({
        uid: file.id,
        url: file.url,
        filename: file.name,
        mimetype: file.filetype,
        size: file.size,
      }));

    const assets = this.generateAssets(listing, images);
    this.setState({
      assets,
      attachments,
      isLoading: false,
      enableOpenHouses: Boolean(enableOpenHouses),
    });
  }

  generateAssets = (
    listing: Pick<
      JsonObject<ListingDoc>,
      | 'city'
      | 'line1'
      | 'neighborhood'
      | 'postal_code'
      | 'province'
      | 'country'
      | 'coords'
    >,
    images: ListingPhotoFile[],
  ) => {
    let assets: CarouselAsset[] = [];
    if (listing.neighborhood !== 'Pending') {
      // @ts-expect-error: number[] is not assignable to type [number, number]
      const address = buildGoogleMapsAddress(listing);
      assets.push({ type: 'map', address });
    }

    const imageAssets = images
      .sort((a, b) => a.order - b.order)
      .filter(image => Boolean(image.url))
      .map<CarouselImageAsset>(image => ({ type: 'image', url: image.url! }));

    assets = assets.concat(imageAssets);

    if (assets.length > 1 && listing.neighborhood !== 'Pending') {
      const [first, second] = assets;
      assets[0] = second;
      assets[1] = first;
    }
    return assets;
  };

  render() {
    const { listing, auth } = this.props;
    const { assets, attachments, isLoading, enableOpenHouses } = this.state;

    if (isLoading) return <Loading size="fa-5x" />;

    return (
      <div style={{ width: '100%' }}>
        <h2 className="font-light m-b-xs">
          <div className="row">
            <ListingTitle listing={listing} />
          </div>
        </h2>
        <hr style={{ marginTop: '.5rem', marginBottom: '.5rem' }} />
        <div className="row">
          <div className="col-md-12">
            <ListingViewCarousel carouselAssets={assets} />
          </div>
        </div>
        <hr />
        <div className="row">
          <PropertyInfo
            listing={listing}
            attachments={attachments}
            enableOpenHouses={enableOpenHouses}
          />
          <ListingDetails listing={listing} />
          <div className="col-md-3">
            <PropertyDetails listing={listing} />
          </div>
        </div>
        {listing.rooms.length > 0 && (
          <div className="row hidden-xs">
            <div className="col-md-12">
              <hr />
              <RoomDetails
                rooms={listing.rooms}
                roomUnit={listing.roomUnit}
                country={auth?.org.country}
              />
            </div>
          </div>
        )}
        {listing.subtype === 'lease' && listing.freehold && (
          <>
            <hr />
            <IncludedAndExcludedList freehold={listing.freehold} />
          </>
        )}
        {listing.type === 'commercial' && listing.more_info && (
          <>
            <hr />
            <MoreInfoList moreInfo={listing.more_info} />
          </>
        )}
        {listing.type === 'commercial' && listing.financials && (
          <>
            <hr />
            <FinancialDetails financials={listing.financials} />
          </>
        )}
        {listing.subtype === 'lease' &&
          (listing.type === 'freehold' || listing.type === 'condo') && (
            <>
              <hr />
              <RequiredList requiredItems={listing.required} />
            </>
          )}
      </div>
    );
  }
}

export default ListingViewContainer;
